import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getIdeaById, 
  voteOnIdea, 
  watchIdea 
} from '../services/ideaService';
import { 
  addComment, 
  fetchComments 
} from '../services/commentService';
import { useAuth } from '../context/AuthContext';
import { 
  ThumbUpIcon, 
  ThumbDownIcon, 
  EyeIcon, 
  ChatIcon, 
  UserIcon,
  StarIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/solid';

const CommentSection = ({ ideaId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  // const { user } = useAuth();

  useEffect(() => {
    const loadComments = async () => {
      try {
        const fetchedComments = await fetchComments(ideaId);
        setComments(fetchedComments);
      } catch (error) {
        console.error('Failed to load comments', error);
      }
    };

    loadComments();
  }, [ideaId]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const comment = await addComment({
        content: newComment,
        ideaId
      });
      setComments([...comments, comment]);
      setNewComment('');
    } catch (error) {
      console.error('Failed to add comment', error);
    }
  };

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Comments ({comments.length})
      </h3>

      {/* Comment Input */}
      <form onSubmit={handleAddComment} className="mb-6">
        <div className="mt-1">
          <textarea
            rows={4}
            name="comment"
            id="comment"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="shadow-sm focus:ring-brand-500 focus:border-brand-500 block w-full sm:text-sm border-gray-300 rounded-md"
            placeholder="Add your comment"
          />
        </div>
        <div className="mt-2 flex justify-end">
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
          >
            Post Comment
          </button>
        </div>
      </form>

      {/* Comments List */}
      {comments.length === 0 ? (
        <p className="text-gray-500 text-center">No comments yet</p>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div 
              key={comment._id} 
              className="bg-white shadow rounded-lg p-4"
            >
              <div className="flex items-center mb-2">
                <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-700">
                  {comment.author.name}
                </span>
              </div>
              <p className="text-gray-600">{comment.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const IdeaDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [idea, setIdea] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isWatching, setIsWatching] = useState(false);
  const [userVote, setUserVote] = useState(null);

  useEffect(() => {
    const fetchIdea = async () => {
      try {
        if (!id) {
          throw new Error('Invalid idea ID');
        }

        setIsLoading(true);
        const fetchedIdea = await getIdeaById(id);
        
        if (!fetchedIdea) {
          throw new Error('Idea not found');
        }

        setIdea(fetchedIdea);
        setIsLoading(false);
        
        // Check if user is watching
        setIsWatching(fetchedIdea.watchers?.includes(user?._id) || false);
        
        // Determine user's current vote
        const userVoteOnIdea = fetchedIdea.votes?.find(
          vote => vote.user.toString() === user?._id
        );
        setUserVote(userVoteOnIdea?.voteType || null);
      } catch (error) {
        console.error('Error fetching idea:', error);
        setError(error.message || 'Failed to load idea');
        setIsLoading(false);
      }
    };

    fetchIdea();
  }, [id, user]);

  const handleVote = async (voteType) => {
    try {
      const voteResponse = await voteOnIdea(id, voteType);
      
      // Update idea with new vote counts
      setIdea(prevIdea => ({
        ...prevIdea,
        upvoteCount: voteResponse.upvoteCount,
        downvoteCount: voteResponse.downvoteCount
      }));

      // Update user's current vote
      setUserVote(voteResponse.userVote);
    } catch (error) {
      console.error('Vote failed', error);
    }
  };

  const toggleWatching = async () => {
    try {
      await watchIdea(id, !isWatching);
      setIsWatching(!isWatching);
    } catch (error) {
      console.error('Watch toggle failed', error);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-brand-600"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 text-center text-red-500">
        <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
        <p className="text-xl mb-4">{error}</p>
        <button 
          onClick={() => navigate('/dashboard')}
          className="bg-brand-600 text-white px-4 py-2 rounded hover:bg-brand-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  // No idea found state
  if (!idea) {
    return (
      <div className="container mx-auto px-4 py-8 text-center text-red-500">
        <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
        <p className="text-xl mb-4">No idea found</p>
        <button 
          onClick={() => navigate('/dashboard')}
          className="bg-brand-600 text-white px-4 py-2 rounded hover:bg-brand-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto bg-white shadow-md rounded-lg p-8">
        {/* Back Button */}
        <div className="mb-6 flex items-center">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-brand-600 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
        </div>

        {/* Idea Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {idea.title}
            </h2>
            {idea.isAnonymous ? (
              <span className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded-full">
                Anonymous
              </span>
            ) : (
              <span className="text-sm text-gray-600">
                By {idea.author?.name || 'Unknown Author'}
              </span>
            )}
          </div>

          {/* Idea Metadata */}
          <div className="flex items-center space-x-4 text-gray-600 text-sm mt-2">
            <span className="flex items-center">
              <EyeIcon className="h-4 w-4 mr-1" />
              {idea.views || 0} Views
            </span>
            <span className="flex items-center">
              <ChatIcon className="h-4 w-4 mr-1" />
              {idea.comments || 0} Comments
            </span>
          </div>
        </div>

        {/* Idea Content */}
        <div className="prose max-w-none mb-6">
          <p>{idea.content}</p>
        </div>

        {/* Tags */}
        {idea.tags && idea.tags.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {idea.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-4 mb-6">
          {/* Upvote Button */}
          <button
            onClick={() => handleVote('upvote')}
            className={`flex items-center space-x-1 text-sm ${
              userVote === 'upvote' 
                ? 'text-brand-600 font-bold' 
                : 'text-gray-700 hover:text-brand-600'
            }`}
          >
            <ThumbUpIcon className="h-5 w-5" />
            <span>{idea.upvoteCount || 0}</span>
          </button>

          {/* Downvote Button */}
          <button
            onClick={() => handleVote('downvote')}
            className={`flex items-center space-x-1 text-sm ${
              userVote === 'downvote' 
                ? 'text-red-600 font-bold' 
                : 'text-gray-700 hover:text-red-600'
            }`}
          >
            <ThumbDownIcon className="h-5 w-5" />
            <span>{idea.downvoteCount || 0}</span>
          </button>

          {/* Watch/Unwatch Button */}
          <button
            onClick={toggleWatching}
            className={`flex items-center space-x-1 text-sm ${
              isWatching 
                ? 'text-brand-600 hover:text-brand-700' 
                : 'text-gray-700 hover:text-brand-600'
            }`}
          >
            <StarIcon className="h-5 w-5" />
            <span>{isWatching ? 'Unwatch' : 'Watch'}</span>
          </button>
        </div>

        {/* Comments Section */}
        <CommentSection ideaId={id} />
      </div>
    </div>
  );
};

export default IdeaDetailPage;